import { useState, useRef, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
    Target, Database, Map, Settings, BarChart3, Eye, X,
    PanelLeftClose, PanelLeftOpen, ChevronLeft,
    Settings2, Building2, Users, Mail, CreditCard, ScrollText, MapPin,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { WorkspaceSwitcher } from '@/components/workspace'
import { useWorkspace } from '@/contexts/WorkspaceContext'

// ============================================================================
// Types
// ============================================================================

interface SidebarProps {
    collapsed: boolean
    onCollapsedChange: (collapsed: boolean) => void
    /** Mobile overlay mode — controlled by AppLayout */
    mobileOpen: boolean
    onMobileClose: () => void
}

interface NavItem {
    icon: React.ComponentType<{ className?: string }>
    label: string
    path: string
    matchPrefix?: boolean
    end?: boolean
    adminOnly?: boolean
}

// ============================================================================
// Navigation config
// ============================================================================

/** Core analytics views — tightly grouped in the main nav area */
const analyticsItems: NavItem[] = [
    { icon: Target, label: 'Competitor', path: '/app/competitor' },
    { icon: BarChart3, label: 'Availability', path: '/app/availability' },
    { icon: Eye, label: 'Visibility', path: '/app/visibility' },
    { icon: Database, label: 'Performance', path: '/app/performance' },
    { icon: Map, label: 'Store Maps', path: '/app/store-maps' },
]

/** Global config — rendered at the bottom, above the collapse toggle */
const settingsItem: NavItem = { icon: Settings, label: 'Settings', path: '/app/settings', matchPrefix: true }

/** Settings sub-navigation — Module Settings */
const settingsModuleItems: NavItem[] = [
    { icon: Settings2, label: 'Competitor', path: '/app/settings', end: true },
    { icon: BarChart3, label: 'Availability', path: '/app/settings/availability' },
    { icon: Eye, label: 'Visibility', path: '/app/settings/visibility' },
    { icon: ScrollText, label: 'Job Logs', path: '/app/settings/job-logs' },
    { icon: MapPin, label: 'Geo Mappings', path: '/app/settings/geo-mappings' },
]

/** Settings sub-navigation — Workspace */
const settingsWorkspaceItems: NavItem[] = [
    { icon: Building2, label: 'General', path: '/app/settings/workspace', end: true },
    { icon: Users, label: 'Team', path: '/app/settings/workspace/team' },
    { icon: Mail, label: 'Invites', path: '/app/settings/workspace/invites', adminOnly: true },
    { icon: CreditCard, label: 'Billing', path: '/app/settings/workspace/billing', adminOnly: true },
]

// ============================================================================
// Nav item renderer
// ============================================================================

function renderNavItem(
    item: NavItem,
    isNarrowMode: boolean,
    pathname: string,
    onClick: () => void,
) {
    const Icon = item.icon
    const isActive = item.end
        ? pathname === item.path
        : item.matchPrefix
            ? pathname.startsWith(item.path)
            : pathname === item.path || pathname.startsWith(item.path + '/')

    const link = (
        <NavLink
            to={item.path}
            end={item.end}
            onClick={onClick}
            className={cn(
                'group relative flex items-center rounded-lg transition-all duration-200',
                isNarrowMode ? 'justify-center px-0 py-2' : 'gap-2.5 pl-3.5 pr-3 py-2',
                isActive
                    ? 'bg-primary/[0.09] dark:bg-primary/20 text-primary font-semibold'
                    : 'text-muted-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.07] hover:text-foreground'
            )}
        >
            {/* Active accent bar */}
            {isActive && !isNarrowMode && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-primary" />
            )}
            <Icon className={cn("h-[18px] w-[18px] flex-shrink-0", isActive && "text-primary")} />
            {!isNarrowMode && (
                <span className="text-[13px] font-medium leading-normal truncate">
                    {item.label}
                </span>
            )}
        </NavLink>
    )

    if (isNarrowMode) {
        return (
            <Tooltip key={item.path}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right" className="font-medium text-xs z-[200]">
                    {item.label}
                </TooltipContent>
            </Tooltip>
        )
    }

    return <div key={item.path}>{link}</div>
}

// ============================================================================
// Sidebar component
// ============================================================================

export function Sidebar({ collapsed, onCollapsedChange, mobileOpen, onMobileClose }: SidebarProps) {
    const location = useLocation()
    const { currentWorkspace, isAdmin } = useWorkspace()

    // Detect if we're on a settings page
    const isOnSettings = location.pathname.startsWith('/app/settings')

    // Filter admin-only workspace items
    const visibleWorkspaceItems = settingsWorkspaceItems.filter(
        item => !item.adminOnly || isAdmin
    )

    // Hover-expand state: tracks whether sidebar is temporarily expanded via hover
    const [hoverExpanded, setHoverExpanded] = useState(false)
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // The sidebar appears expanded if it's pinned open OR hover-expanded
    const isVisuallyCollapsed = collapsed && !hoverExpanded

    const handleMouseEnterSidebar = useCallback(() => {
        if (!collapsed) return // already pinned open, nothing to do
        if (collapseTimerRef.current) {
            clearTimeout(collapseTimerRef.current)
            collapseTimerRef.current = null
        }
        setHoverExpanded(true)
    }, [collapsed])

    const handleMouseLeaveSidebar = useCallback(() => {
        if (!collapsed) return // pinned open, don't auto-collapse
        // Small delay to prevent flicker when moving between elements
        collapseTimerRef.current = setTimeout(() => {
            setHoverExpanded(false)
        }, 200)
    }, [collapsed])

    // Close mobile drawer on navigation
    const handleNavClick = () => {
        if (mobileOpen) onMobileClose()
    }

    const toggleCollapse = () => {
        if (!collapsed) {
            // Pinned open -> collapse & clear hover
            setHoverExpanded(false)
            onCollapsedChange(true)
        } else {
            // Collapsed -> pin open & clear hover state
            setHoverExpanded(false)
            onCollapsedChange(false)
        }
    }

    // ── Shared sidebar content (used by both desktop & mobile) ──────────
    const sidebarContent = (isMobile: boolean) => {
        const isNarrowMode = isVisuallyCollapsed && !isMobile

        return (
            <div className="flex flex-col h-full">
                {/* ─── Logo Section ─── */}
                <div className={cn(
                    "h-14 flex items-center flex-shrink-0 transition-all duration-300",
                    isNarrowMode ? "justify-center px-2" : "justify-between px-4"
                )}>
                    {isNarrowMode ? (
                        /* Collapsed: logo — hover to expand */
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="flex items-center justify-center h-9 w-9 rounded-xl hover:bg-white/30 dark:hover:bg-white/10 transition-colors"
                                >
                                    <img src="/ReflowAI Logo.png" alt="ReflowAI" className="h-7 w-7 rounded-lg object-contain" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right">Hover to expand</TooltipContent>
                        </Tooltip>
                    ) : (
                        /* Expanded / Mobile: logo + collapse, plus X on mobile */
                        <>
                            <button
                                onClick={isMobile ? undefined : toggleCollapse}
                                className={cn(
                                    "flex gap-2.5 items-center min-w-0",
                                    !isMobile && "rounded-xl hover:bg-white/20 dark:hover:bg-white/[0.06] -ml-1 pl-1 pr-2 py-1 transition-colors cursor-pointer"
                                )}
                            >
                                <img src="/ReflowAI Logo.png" alt="ReflowAI" className="h-7 w-7 rounded-lg object-contain flex-shrink-0" />
                                <div className="flex flex-col min-w-0">
                                    <h1 className="text-foreground text-[13px] font-bold leading-none tracking-tight truncate">
                                        ReflowAI
                                    </h1>
                                    <p className="text-muted-foreground text-[10px] font-medium leading-tight mt-0.5">
                                        Intelligence
                                    </p>
                                </div>
                            </button>
                            {/* Mobile only: close button */}
                            {isMobile && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={onMobileClose}
                                    className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-white/30 dark:hover:bg-white/10 flex-shrink-0 rounded-lg transition-all"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </>
                    )}
                </div>

                {/* ─── Workspace / Brand Selector ─── */}
                {/* Distinct from navigation: faint bg + heavier separator below */}
                <div className={cn(
                    "transition-all duration-300",
                    isNarrowMode ? "px-1.5 py-2" : "px-2.5 py-2"
                )}>
                    <div className={cn(
                        "rounded-xl transition-all duration-200",
                        isNarrowMode
                            ? ""
                            : "bg-white/25 dark:bg-white/[0.06] ring-1 ring-black/[0.04] dark:ring-white/[0.08]"
                    )}>
                        <WorkspaceSwitcher collapsed={isNarrowMode} />
                    </div>
                </div>
                {/* Separator: clearly divides context-selector from page nav */}
                <div className={cn(
                    "transition-all duration-300",
                    isNarrowMode ? "mx-2" : "mx-3"
                )}>
                    <div className="border-b border-black/[0.08] dark:border-white/[0.12]" />
                </div>

                {isOnSettings ? (
                    <>
                        {/* ─── Back to Dashboard ─── */}
                        <div className={cn(
                            "transition-all duration-300",
                            isNarrowMode ? "px-1.5 pt-2" : "px-2.5 pt-3"
                        )}>
                            {isNarrowMode ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <NavLink
                                            to="/app/competitor"
                                            onClick={handleNavClick}
                                            className="flex items-center justify-center rounded-lg py-2 text-muted-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.07] hover:text-foreground transition-all duration-200"
                                        >
                                            <ChevronLeft className="h-[18px] w-[18px]" />
                                        </NavLink>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="font-medium text-xs z-[200]">Back to Dashboard</TooltipContent>
                                </Tooltip>
                            ) : (
                                <NavLink
                                    to="/app/competitor"
                                    onClick={handleNavClick}
                                    className="flex items-center gap-2 rounded-lg pl-3.5 pr-3 py-2 text-muted-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.07] hover:text-foreground transition-all duration-200 text-[13px] font-medium"
                                >
                                    <ChevronLeft className="h-[18px] w-[18px] flex-shrink-0" />
                                    <span className="truncate">Back to Dashboard</span>
                                </NavLink>
                            )}
                        </div>

                        {/* ─── Settings Navigation ─── */}
                        <nav className={cn(
                            "flex-1 flex flex-col gap-0.5 overflow-y-auto custom-scrollbar transition-all duration-300",
                            isNarrowMode ? "p-1.5 pt-1" : "p-2.5 pt-2"
                        )}>
                            {/* Workspace section */}
                            {currentWorkspace && (
                                <>
                                    {!isNarrowMode && (
                                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3.5 mb-1 mt-1">
                                            Workspace
                                        </p>
                                    )}
                                    {visibleWorkspaceItems.map((item) => renderNavItem(item, isNarrowMode, location.pathname, handleNavClick))}

                                    {/* Separator */}
                                    <div className={cn("my-2 transition-all duration-300", isNarrowMode ? "mx-1" : "mx-2")}>
                                        <div className="border-b border-black/[0.08] dark:border-white/[0.12]" />
                                    </div>
                                </>
                            )}

                            {/* Module Settings section */}
                            {!isNarrowMode && (
                                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3.5 mb-1">
                                    Module Settings
                                </p>
                            )}
                            {settingsModuleItems.map((item) => renderNavItem(item, isNarrowMode, location.pathname, handleNavClick))}
                        </nav>
                    </>
                ) : (
                    <>
                        {/* ─── Analytics Navigation (core views) ─── */}
                        <nav className={cn(
                            "flex-1 flex flex-col gap-0.5 overflow-y-auto custom-scrollbar transition-all duration-300",
                            isNarrowMode ? "p-1.5 pt-2" : "p-2.5 pt-3"
                        )}>
                            {analyticsItems.map((item) => renderNavItem(item, isNarrowMode, location.pathname, handleNavClick))}
                        </nav>

                        {/* ─── Settings (global config, separated from analytics) ─── */}
                        <div className={cn(
                            "transition-all duration-300",
                            isNarrowMode ? "px-1.5 pb-0.5" : "px-2.5 pb-0.5"
                        )}>
                            {renderNavItem(settingsItem, isNarrowMode, location.pathname, handleNavClick)}
                        </div>
                    </>
                )}

                {/* ─── Bottom: Collapse/Expand toggle (desktop only) ─── */}
                {!isMobile && (
                    <div className={cn(
                        "border-t border-black/[0.08] dark:border-white/[0.12] transition-all duration-300",
                        isNarrowMode ? "p-1.5" : "p-2.5"
                    )}>
                        {isNarrowMode ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleCollapse}
                                        className="w-full flex items-center justify-center h-8 rounded-xl text-muted-foreground hover:text-foreground hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-200"
                                    >
                                        <PanelLeftOpen className="h-[18px] w-[18px]" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="text-xs z-[200]">Pin sidebar open</TooltipContent>
                            </Tooltip>
                        ) : (
                            <button
                                onClick={toggleCollapse}
                                className="w-full flex items-center gap-2.5 px-3 h-8 rounded-xl text-muted-foreground hover:text-foreground hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-200"
                            >
                                <PanelLeftClose className="h-[18px] w-[18px] flex-shrink-0" />
                                <span className="text-[13px] font-medium">Collapse</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <TooltipProvider delayDuration={0}>
            {/* ─── Desktop Sidebar (lg and up) ─── */}
            <aside
                onMouseEnter={handleMouseEnterSidebar}
                onMouseLeave={handleMouseLeaveSidebar}
                className={cn(
                    // Hidden on mobile, visible on lg+
                    'hidden lg:flex flex-col h-full relative flex-shrink-0 z-10',
                    // Glass surface
                    'bg-white/40 dark:bg-black/30 backdrop-blur-2xl backdrop-saturate-150',
                    'border-r border-white/30 dark:border-white/10',
                    'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15),0_4px_24px_rgba(0,0,0,0.06)]',
                    // Smooth width transition
                    'transition-[width] duration-300 ease-out',
                    isVisuallyCollapsed ? 'w-16' : 'w-[220px]',
                )}
            >
                {sidebarContent(false)}
            </aside>

            {/* ─── Mobile Drawer (below lg) ─── */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
                        onClick={onMobileClose}
                    />
                    {/* Drawer panel */}
                    <aside
                        className={cn(
                            'absolute left-0 top-0 h-full w-[260px] flex flex-col',
                            'bg-white/70 dark:bg-black/60 backdrop-blur-2xl backdrop-saturate-150',
                            'border-r border-white/30 dark:border-white/10',
                            'shadow-2xl',
                            'animate-in slide-in-from-left duration-300 ease-out',
                        )}
                    >
                        {sidebarContent(true)}
                    </aside>
                </div>
            )}
        </TooltipProvider>
    )
}
